/**
 * Learnings (M1.5): reviewer findings improve the pipeline. The Studio PROPOSES a rule; the owner approves or declines it here;
 * an approved rule is written into the Studio's doctrine on a `learning/<id>` branch, the runner opens a PR, and the owner merges.
 */
import { useState } from "react";
import { Link } from "../../app/router";
import type { Learning, LearningStatus } from "../../api/types";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/toast";
import { Badge, Button, EmptyState, Field, Modal, PageHeader, QueryView } from "../../components/ui";
import { useApi, useMutation } from "../../data/hooks";
import { useLearnings } from "../../data/queries";
import { dateTime, relative } from "../../lib/format";
import type { Tone } from "../../lib/status";

const GROUPS: { status: LearningStatus; title: string; hint: string; tone: Tone }[] = [
  { status: "proposed", title: "Proposed", hint: "Rules the Studio suggests from reviewer findings. Approve one and the Studio writes it into its doctrine.", tone: "attention" },
  { status: "confirmed", title: "Approved — in progress", hint: "The Studio is writing these into its doctrine on a branch.", tone: "info" },
  { status: "applied", title: "Applied", hint: "Written and pushed. Merge the pull request to make it part of every future build.", tone: "ok" },
  { status: "declined", title: "Declined", hint: "Not adopted. Kept for the record.", tone: "neutral" },
];

export function LearningsScreen() {
  const q = useLearnings();
  return (
    <div className="stack-lg">
      <PageHeader title="Learnings" subtitle="What the Studio learns from reviews. You decide which rules it adopts; nothing changes until you merge the pull request." />
      <QueryView q={q}>{(items) => items.length ? <LearningGroups items={items} /> : (
        <EmptyState icon="bulb" title="No learnings yet">When a reviewer finding points at something the Studio should always do, it proposes a rule here for you to approve.</EmptyState>
      )}</QueryView>
    </div>
  );
}

function LearningGroups({ items }: { items: Learning[] }) {
  const [deciding, setDeciding] = useState<{ learning: Learning; action: "approve" | "decline" } | null>(null);
  return (
    <>
      {GROUPS.map((g) => {
        const list = items.filter((l) => l.status === g.status).sort((a, b) => b.created_at.localeCompare(a.created_at));
        if (!list.length) return null;
        const body = (
          <>
            <p className="muted small">{g.hint}</p>
            <ul className="cards">{list.map((l) => <li key={l.id}><LearningCard l={l} tone={g.tone} onDecide={(action) => setDeciding({ learning: l, action })} /></li>)}</ul>
          </>
        );
        const heading = <>{g.title} <span className="count-pill">{list.length}</span></>;
        return g.status === "declined" ? (
          <details key={g.status} className="learning-group learning-group-folded">
            <summary><h2>{heading}</h2><Icon name="chevronDown" size={18} /></summary>
            {body}
          </details>
        ) : (
          <section key={g.status} className="learning-group" aria-labelledby={`lg-${g.status}`}>
            <h2 id={`lg-${g.status}`}>{heading}</h2>
            {body}
          </section>
        );
      })}
      {deciding && <DecideSheet learning={deciding.learning} action={deciding.action} onClose={() => setDeciding(null)} />}
    </>
  );
}

export function LearningCard({ l, tone, onDecide }: { l: Learning; tone: Tone; onDecide: (a: "approve" | "decline") => void }) {
  return (
    <article className={`learning tone-${tone}`} aria-label={l.rule}>
      <div className="learning-head">
        <Badge tone="neutral">{l.scope.replace(/_/g, " ")}</Badge>
        {l.series && <Link to={`/studio/${l.series.id}`} className="learning-series">{l.series.title}</Link>}
        <time className="muted small push" dateTime={l.created_at} title={dateTime(l.created_at)}>{relative(l.created_at)}</time>
      </div>
      <h3 className="learning-rule">{l.rule}</h3>
      <div className="learning-evidence">
        <span className="eyebrow">Evidence</span>
        <p className="pre">{l.evidence}</p>
      </div>
      {l.files.length > 0 && (
        <div className="learning-files">
          <span className="eyebrow">Files</span>
          <ul>{l.files.map((f) => <li key={f}><code>{f}</code></li>)}</ul>
        </div>
      )}
      {l.decision_note && <p className="note"><b>Your note:</b> {l.decision_note}</p>}

      {l.status === "proposed" && (
        <div className="learning-actions">
          <Button variant="primary" icon="check" onClick={() => onDecide("approve")}>Approve</Button>
          <Button variant="secondary" onClick={() => onDecide("decline")}>Decline</Button>
        </div>
      )}
      {l.status === "confirmed" && (
        <p className="learning-status"><span className="spinner" aria-hidden /> <span>Approved{l.decided_at ? ` ${relative(l.decided_at)}` : ""}. The Studio is writing this into its doctrine…</span></p>
      )}
      {l.status === "applied" && l.applied && (
        <div className="learning-applied">
          {l.applied.pr_url
            ? <a className="btn btn-primary btn-md" href={l.applied.pr_url} target="_blank" rel="noopener noreferrer"><Icon name="external" size={20} /><span>Pull request ready: merge on GitHub</span></a>
            : <p className="small">Branch pushed — the pull request link appears here once it's open.</p>}
          <p className="muted small learning-ref"><Icon name="branch" size={16} /> <code>{l.applied.branch}</code> · commit <code>{l.applied.commit.slice(0, 7)}</code></p>
        </div>
      )}
      {l.status === "declined" && <p className="muted small">Declined{l.decided_at ? ` ${relative(l.decided_at)}` : ""}.</p>}
    </article>
  );
}

function DecideSheet({ learning, action, onClose }: { learning: Learning; action: "approve" | "decline"; onClose: () => void }) {
  const api = useApi();
  const toast = useToast();
  const [note, setNote] = useState("");
  const approve = action === "approve";
  const decide = useMutation((n: string) => (approve ? api.approveLearning : api.declineLearning)(learning.id, n ? { note: n } : {}), {
    invalidate: ["learnings", "dashboard"],
    onSuccess: () => { toast.ok(approve ? "Approved — the Studio will write it into its doctrine and open a pull request." : "Declined."); onClose(); },
    onError: (e) => toast.error(e, approve ? "Couldn't approve" : "Couldn't decline"),
  });
  return (
    <Modal variant="sheet" title={approve ? "Approve this learning?" : "Decline this learning?"} onClose={onClose} footer={
      <>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant={approve ? "primary" : "danger"} busy={decide.pending} onClick={() => void decide.mutate(note.trim())}>{approve ? "Approve" : "Decline"}</Button>
      </>
    }>
      <p className="learning-rule">{learning.rule}</p>
      <p className="impact muted small">{approve
        ? "The Studio writes this rule into its doctrine on its own branch and opens a pull request. Nothing changes for future builds until you merge it."
        : "The Studio won't adopt this rule. Tell it why, so it doesn't propose the same thing again."}</p>
      <Field label={approve ? "Note for the Studio" : "Why not?"} optional>
        {(id) => <textarea id={id} rows={3} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} placeholder={approve ? "e.g. Only for Faith series" : "e.g. This was a one-off; the reviewer misheard"} />}
      </Field>
    </Modal>
  );
}
